<script setup>
import BaseIcon from "@/app/components/ui/BaseIcon.vue"
import { useI18n } from "@/app/composables/useI18n"
import { computed, ref } from "vue"

const props = defineProps({
  show: Boolean,
})

const emit = defineEmits(["close"])

const { t, locales, loadLanguage } = useI18n()

const searchTerm = ref("")

const filteredLocales = computed(() => {
  if (!locales.value || !Array.isArray(locales.value)) {
    return []
  }

  if (!searchTerm.value || searchTerm.value.trim() === "") {
    return locales.value
  }

  const search = searchTerm.value.toLowerCase().trim()
  return locales.value.filter(locale =>
    locale.name.toLowerCase().includes(search)
    || locale.code.toLowerCase().includes(search)
    || locale.friendly_name.toLowerCase().includes(search),
  )
})

function changeLocale(langCode) {
  loadLanguage(langCode)
  emit("close", false)
}

function clearSearch() {
  searchTerm.value = ""
}
</script>

<template>
  <Transition name="modal">
    <div v-if="props.show" class="modal-mask">
      <div class="modal-container">
        <div class="modal-detail">
          <div
            class="close-btn"
            @click="emit('close', false)"
          >
            <div>{{ t("general.close") }}</div>
            <img src="/assets/images/close-modal-icon.svg" alt="">
          </div>
          <div class="modal-body">
            <!-- Search Bar -->
            <div class="search-container">
              <div class="search-input-wrapper">
                <BaseIcon name="magnify" size="16" class="search-icon" />
                <input
                  v-model="searchTerm"
                  type="text"
                  :placeholder="t('casino.search')"
                  class="search-input"
                >
                <BaseIcon
                  v-if="searchTerm"
                  name="close"
                  size="24"
                  class="clear-search-btn"
                  @click="clearSearch"
                />
              </div>
            </div>

            <!-- Languages List -->
            <div class="locales">
              <div
                v-for="(locale, index) in filteredLocales"
                :key="index"
                class="modal-body-item"
                @click="changeLocale(locale.code)"
              >
                <div class="modal-body-item-title">
                  <img
                    class="league-flag"
                    :src="`/assets/images/flags/${locale.code}.svg`"
                    alt=""
                  >
                  <div>{{ locale.name }}</div>
                </div>
              </div>

              <!-- No Results Message -->
              <div v-if="filteredLocales.length === 0" class="no-results">
                <div>{{ t('general.no_results') || 'No languages found' }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  // background: rgba(13, 12, 44, 0.5);
  backdrop-filter: blur(5px);
  display: flex;
  transition: opacity 0.3s ease;
}

.close-btn {
  color: white;
  position: absolute;
  top: -32px;
  right: 3px;
  display: flex;
  gap: 0 5px;
  cursor: pointer;
}

.modal-container {
  width: 491px;
  margin: auto;
  padding: 39px 20px 20px 20px;

  height: 100%;
  overflow: auto;
  // background: var(--be-bg);

  transition: all 0.3s ease;
  position: relative;
  display: flex;
}

.modal-detail {
  background: var(--be-bg);
  padding: 20px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  margin-top: 13px;
  border-radius: 20px;
  margin: auto;
  position: relative;
  width: 100%;
}

.modal-header h3 {
  margin-top: 0;
  color: #42b983;
  border-radius: 8px;
  background: var(--be-big-card-bg);
}

.modal-body {
  border-radius: 8px;
  background: var(--be-big-card-bg);
  padding: 16px;
}

.modal-default-button {
  float: right;
}

.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}

.visitor-info {
  margin: 10px 0;
  padding: 10px;
  border-radius: 4px;
  background: var(--be-small-card-bg);
  font-size: 14px;

  p {
    margin: 5px 0;
  }
}

.search-container {
  margin-bottom: 16px;

  .search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;

    .search-icon {
      position: absolute;
      left: 12px;
      opacity: 0.6;
      z-index: 1;
      pointer-events: none;
    }

    .search-input {
      width: 100%;
      padding: 12px 16px 12px 40px;
      background: var(--be-small-card-bg);
      border: 1px solid var(--be-divider);
      border-radius: 8px;
      color: white;
      font-size: 14px;
      outline: none;
      transition: border-color 0.2s ease;

      &::placeholder {
        color: rgba(255, 255, 255, 0.5);
      }

      &:focus {
        border-color: var(--be-primary, #007bff);
      }
    }

        .clear-search-btn {
      position: absolute;
      right: 12px;
      color: rgba(255, 255, 255, 0.6);
      cursor: pointer;
      padding: 4px;
      border-radius: 50%;
      transition: background-color 0.2s ease, color 0.2s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: white;
      }
    }
  }
}

.locales {
  max-height: calc(100vh - 330px);
  overflow-y: auto;

  .modal-body-item {
    display: flex;
    align-items: center;
    padding: 10px;
    cursor: pointer;
    border-radius: 8px;
    transition: background-color 0.2s ease;

    &:hover {
      background: var(--be-divider);
    }

    .modal-body-item-title {
      display: flex;
      align-items: center;
      gap: 15px;

      .league-flag {
        width: 24px;
        height: 24px;
        border-radius: 5px;
      }
    }
  }

  .no-results {
    text-align: center;
    padding: 40px 20px;
    color: rgba(255, 255, 255, 0.6);
    font-style: italic;
  }
}

/* Hide scrollbar but keep functionality */
.locales {
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}
</style>
